/**
 * Internal tool — QR code art generator for arenas.
 *
 * Operators upload an arena logo, paste the arena's booking page URL and pick a
 * background color; the page composes a ready-to-print art piece: arena logo on
 * top, a styled QR code (rounded modules + Meiuca symbol in the center) pointing
 * to the booking URL, and a discreet Meiuca lockup at the bottom. The art is
 * exported as a high-resolution PNG suitable for stickers/banners.
 *
 * Public route (no auth) at `/tools/qr-art`. Temporary home inside the app web —
 * the definitive location is still to be decided.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import QRCodeStyling from 'qr-code-styling'
import {
  Button,
  ButtonVariant,
  buildBrandedQrOptions,
  MEIUCA_SYMBOL_PATHS,
  MEIUCA_DARK,
} from '@meiuca/ui-components'
import { arenaService, type VenueSearchItem } from '../services/arenaService'

// The QR model + brand symbol/color are shared from the design system so the
// tournament event QR and this promo art stay identical (`@meiuca/ui-components`).

// --- Helpers -------------------------------------------------------------------

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}

/** Relative luminance of a #rrggbb color (0 = black, 1 = white). */
function luminance(hex: string): number {
  const m = hex.replace('#', '')
  const r = parseInt(m.slice(0, 2), 16) / 255
  const g = parseInt(m.slice(2, 4), 16) / 255
  const b = parseInt(m.slice(4, 6), 16) / 255
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b)
}

/** Fit (contain) dimensions for an image within a box, preserving aspect ratio. */
function fitContain(img: HTMLImageElement, maxW: number, maxH: number) {
  const ratio = Math.min(maxW / img.width, maxH / img.height)
  return { w: img.width * ratio, h: img.height * ratio }
}

/** Linear sRGB mix of two #rrggbb colors — `t` is the weight of `b` (color-mix). */
function mixHex(a: string, b: string, t: number) {
  const pa = a.replace('#', '')
  const pb = b.replace('#', '')
  const ch = (i: number) =>
    Math.round(parseInt(pa.slice(i, i + 2), 16) * (1 - t) + parseInt(pb.slice(i, i + 2), 16) * t)
  const to = (x: number) => x.toString(16).padStart(2, '0')
  return `#${to(ch(0))}${to(ch(2))}${to(ch(4))}`
}

/** #rrggbb → rgba() string at the given alpha. */
function hexToRgbA(hex: string, alpha: number) {
  const m = hex.replace('#', '')
  return `rgba(${parseInt(m.slice(0, 2), 16)},${parseInt(m.slice(2, 4), 16)},${parseInt(m.slice(4, 6), 16)},${alpha})`
}

/** Strokes a 24×24 line-icon (SVG-style) scaled into a box at (x, y). */
function strokeIcon(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  color: string,
  draw: (c: CanvasRenderingContext2D) => void,
) {
  ctx.save()
  ctx.translate(x, y)
  ctx.scale(size / 24, size / 24)
  ctx.strokeStyle = color
  ctx.lineWidth = 2
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  draw(ctx)
  ctx.restore()
}

const drawCalendarIcon = (c: CanvasRenderingContext2D) => {
  c.beginPath()
  c.roundRect(3, 4, 18, 18, 3)
  c.stroke()
  c.beginPath()
  c.moveTo(16, 2)
  c.lineTo(16, 6)
  c.moveTo(8, 2)
  c.lineTo(8, 6)
  c.moveTo(3, 10)
  c.lineTo(21, 10)
  c.stroke()
}

const drawCameraIcon = (c: CanvasRenderingContext2D) => {
  c.stroke(
    new Path2D(
      'M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z',
    ),
  )
  c.beginPath()
  c.arc(12, 13, 4, 0, Math.PI * 2)
  c.stroke()
}

/**
 * Draws the Meiuca mark (symbol + "meiuca" wordmark) horizontally centered on
 * `cx`, in a single solid `color` (so it can match the headline — black or white
 * — instead of the brand green that may clash with the chosen background).
 * Returns the total width drawn.
 */
function drawMeiucaMark(
  ctx: CanvasRenderingContext2D,
  cx: number,
  topY: number,
  height: number,
  color: string,
) {
  const gap = height * 0.28
  const fontSize = height * 0.96
  ctx.font = `600 ${fontSize}px 'Inter','Helvetica Neue',system-ui,sans-serif`
  ctx.letterSpacing = `${-fontSize * 0.02}px`
  const word = 'meiuca'
  const textW = ctx.measureText(word).width
  const totalW = height + gap + textW
  const startX = cx - totalW / 2

  ctx.fillStyle = color
  ctx.save()
  ctx.translate(startX, topY)
  ctx.scale(height / 100, height / 100)
  for (const d of MEIUCA_SYMBOL_PATHS) ctx.fill(new Path2D(d))
  ctx.restore()

  ctx.textAlign = 'left'
  ctx.textBaseline = 'middle'
  ctx.fillText(word, startX + height + gap, topY + height / 2)
  ctx.letterSpacing = '0px'
  return totalW
}

interface ArtInputs {
  arenaImg: HTMLImageElement | null
  qrImg: HTMLImageElement | null
  bgColor: string
}

const FONT = "'Inter','Helvetica Neue',system-ui,sans-serif"

// Model "placa" layout (px in a 760×1074 reference card) — ported faithfully and
// scaled to fit any canvas. Heights/gaps are summed to scale-to-fit, centered.
const M = {
  logoH: 120,
  logoMaxW: 300,
  gLogoEyebrow: 44,
  eyebrowH: 22,
  eyebrowFont: 17,
  eyebrowBarW: 30,
  eyebrowBarH: 3,
  eyebrowGap: 12,
  eyebrowTrack: 0.32,
  gEyebrowHead: 22,
  headFont: 88,
  headH: 90,
  headTrack: -0.025,
  gHeadPill: 26,
  pillH: 52,
  pillFont: 22,
  pillPadX: 28,
  pillIcon: 22,
  pillIconGap: 12,
  gPillQr: 64,
  qr: 368,
  qrRadius: 32,
  qrInner: 0.84,
  gQrCaption: 22,
  capH: 24,
  capFont: 20,
  capIcon: 20,
  capGap: 10,
  gCapFooter: 44,
  divW: 64,
  divH: 1.5,
  gDivCredit: 16,
  creditH: 26,
  creditFont: 18,
  creditMarkH: 24,
  creditGap: 9,
}
const MODEL_H =
  M.logoH +
  M.gLogoEyebrow +
  M.eyebrowH +
  M.gEyebrowHead +
  M.headH +
  M.gHeadPill +
  M.pillH +
  M.gPillQr +
  M.qr +
  M.gQrCaption +
  M.capH +
  M.gCapFooter +
  M.divH +
  M.gDivCredit +
  M.creditH

/**
 * Draws the full art onto a `W × H` canvas — the "Placa para Arenas" design:
 * a rich brand-color diagonal gradient + glow + faint padel-court motif, a
 * centered column of arena logo → eyebrow (bars + label) → "Bora jogar" →
 * "Agende seu horário" pill (with calendar icon) → elevated QR card (Meiuca
 * symbol centered) → "Aponte a câmera do celular" → "Reserve em segundos com
 * meiuca". All colors derive from `bgColor` via sRGB mixing so they always
 * harmonize. The whole model layout scales to fit the canvas and is centered, so
 * proportions stay identical across square / story / A4.
 */
function drawArt(ctx: CanvasRenderingContext2D, W: number, H: number, inputs: ArtInputs) {
  const { arenaImg, qrImg, bgColor } = inputs
  const isLight = luminance(bgColor) > 0.5
  const textColor = isLight ? MEIUCA_DARK : '#FFFFFF'
  const u = Math.min(W, H)
  const pad = u * 0.05
  const cx = W / 2

  // --- Color system (sRGB mixes of the chosen color, like the model) ----------
  const g1 = mixHex(bgColor, '#ffffff', 0.14)
  const g3 = mixHex(bgColor, '#000000', 0.36)
  const glowCol = mixHex(bgColor, '#ffffff', 0.45)
  const accent = isLight ? mixHex(bgColor, '#000000', 0.4) : glowCol
  const ink = (a: number) => hexToRgbA(textColor, a)

  // Background: diagonal gradient (≈168°) + soft lower-center glow
  const a = (168 * Math.PI) / 180
  const vx = Math.sin(a)
  const vy = -Math.cos(a)
  const L = Math.abs(vx) * W + Math.abs(vy) * H
  const grad = ctx.createLinearGradient(
    cx - (vx * L) / 2,
    H / 2 - (vy * L) / 2,
    cx + (vx * L) / 2,
    H / 2 + (vy * L) / 2,
  )
  grad.addColorStop(0, g1)
  grad.addColorStop(0.46, bgColor)
  grad.addColorStop(1, g3)
  ctx.fillStyle = grad
  ctx.fillRect(0, 0, W, H)

  const glow = ctx.createRadialGradient(cx, H * 0.62, 0, cx, H * 0.62, u * 0.55)
  glow.addColorStop(0, hexToRgbA(glowCol, isLight ? 0.18 : 0.32))
  glow.addColorStop(1, hexToRgbA(glowCol, 0))
  ctx.fillStyle = glow
  ctx.fillRect(0, 0, W, H)

  // --- Scale the model layout to fit, then center it --------------------------
  ctx.font = `800 ${M.headFont}px ${FONT}`
  ctx.letterSpacing = `${M.headFont * M.headTrack}px`
  const headW = ctx.measureText('Bora jogar').width
  ctx.letterSpacing = '0px'
  ctx.font = `600 ${M.pillFont}px ${FONT}`
  const pillTextW = ctx.measureText('Agende seu horário').width
  const pillModelW = M.pillIcon + M.pillIconGap + pillTextW + M.pillPadX * 2
  ctx.font = `500 ${M.capFont}px ${FONT}`
  const capModelW = M.capIcon + M.capGap + ctx.measureText('Aponte a câmera do celular').width
  ctx.font = `500 ${M.creditFont}px ${FONT}`
  const creditTextW = ctx.measureText('Reserve em segundos com').width
  ctx.font = `600 ${M.creditMarkH * 0.96}px ${FONT}`
  ctx.letterSpacing = `${-M.creditMarkH * 0.96 * 0.02}px`
  const creditMarkW = M.creditMarkH + M.creditMarkH * 0.28 + ctx.measureText('meiuca').width
  ctx.letterSpacing = '0px'
  const creditModelW = creditTextW + M.creditGap + creditMarkW

  const maxModelW = Math.max(M.logoMaxW, M.qr, headW, pillModelW, capModelW, creditModelW)
  const s = Math.min((H - pad * 2) / MODEL_H, (W - pad * 2) / maxModelW)
  const px = (v: number) => v * s

  // Faint padel-court motif — scaled with the content (s) and centered on the
  // canvas, so it stays aligned with the centered column instead of stretching.
  const cdx = (v: number) => cx + (v - 380) * s // 380 = model card center x (760/2)
  const cdy = (v: number) => H / 2 + (v - 537) * s // 537 = model card center y (1074/2)
  ctx.save()
  ctx.strokeStyle = ink(0.06)
  ctx.lineWidth = Math.max(1, u * 0.0025)
  ctx.beginPath()
  ctx.roundRect(cdx(118), cdy(150), 524 * s, 774 * s, 6 * s)
  ctx.moveTo(cdx(118), cdy(537))
  ctx.lineTo(cdx(642), cdy(537))
  ctx.moveTo(cdx(380), cdy(150))
  ctx.lineTo(cdx(380), cdy(924))
  ctx.moveTo(cdx(118), cdy(330))
  ctx.lineTo(cdx(642), cdy(330))
  ctx.moveTo(cdx(118), cdy(744))
  ctx.lineTo(cdx(642), cdy(744))
  ctx.stroke()
  ctx.restore()

  let y = (H - MODEL_H * s) / 2
  ctx.textBaseline = 'top'

  // Logo (centered)
  if (arenaImg) {
    const logo = fitContain(arenaImg, px(M.logoMaxW), px(M.logoH))
    ctx.drawImage(arenaImg, cx - logo.w / 2, y + (px(M.logoH) - logo.h) / 2, logo.w, logo.h)
  }
  y += px(M.logoH) + px(M.gLogoEyebrow)

  // Eyebrow: bar — LABEL — bar (centered)
  ctx.font = `600 ${px(M.eyebrowFont)}px ${FONT}`
  ctx.letterSpacing = `${px(M.eyebrowFont) * M.eyebrowTrack}px`
  const ebText = 'AGENDAMENTO ONLINE'
  const ebTextW = ctx.measureText(ebText).width
  const ebTotal = px(M.eyebrowBarW) * 2 + px(M.eyebrowGap) * 2 + ebTextW
  let ebX = cx - ebTotal / 2
  const ebMid = y + px(M.eyebrowH) / 2
  ctx.fillStyle = accent
  ctx.fillRect(ebX, ebMid - px(M.eyebrowBarH) / 2, px(M.eyebrowBarW), px(M.eyebrowBarH))
  ctx.fillStyle = ink(isLight ? 0.7 : 0.78)
  ctx.textAlign = 'left'
  ctx.textBaseline = 'middle'
  ctx.fillText(ebText, ebX + px(M.eyebrowBarW) + px(M.eyebrowGap), ebMid)
  ebX += px(M.eyebrowBarW) + px(M.eyebrowGap) + ebTextW + px(M.eyebrowGap)
  ctx.fillStyle = accent
  ctx.fillRect(ebX, ebMid - px(M.eyebrowBarH) / 2, px(M.eyebrowBarW), px(M.eyebrowBarH))
  ctx.letterSpacing = '0px'
  ctx.textBaseline = 'top'
  y += px(M.eyebrowH) + px(M.gEyebrowHead)

  // Headline
  ctx.fillStyle = textColor
  ctx.font = `800 ${px(M.headFont)}px ${FONT}`
  ctx.letterSpacing = `${px(M.headFont) * M.headTrack}px`
  ctx.textAlign = 'center'
  ctx.fillText('Bora jogar', cx, y)
  ctx.letterSpacing = '0px'
  y += px(M.headH) + px(M.gHeadPill)

  // Secondary CTA pill (calendar icon + label), centered
  const pillW = px(pillModelW)
  const pillH = px(M.pillH)
  const pillX = cx - pillW / 2
  ctx.fillStyle = ink(isLight ? 0.08 : 0.14)
  ctx.beginPath()
  ctx.roundRect(pillX, y, pillW, pillH, pillH / 2)
  ctx.fill()
  ctx.strokeStyle = ink(isLight ? 0.16 : 0.22)
  ctx.lineWidth = Math.max(1, px(1))
  ctx.beginPath()
  ctx.roundRect(pillX, y, pillW, pillH, pillH / 2)
  ctx.stroke()
  strokeIcon(ctx, pillX + px(M.pillPadX), y + (pillH - px(M.pillIcon)) / 2, px(M.pillIcon), textColor, drawCalendarIcon)
  ctx.fillStyle = textColor
  ctx.font = `600 ${px(M.pillFont)}px ${FONT}`
  ctx.textAlign = 'left'
  ctx.textBaseline = 'middle'
  ctx.fillText('Agende seu horário', pillX + px(M.pillPadX) + px(M.pillIcon) + px(M.pillIconGap), y + pillH / 2)
  ctx.textBaseline = 'top'
  y += pillH + px(M.gPillQr)

  // QR hero — elevated white card with the Meiuca symbol centered
  const cardSize = px(M.qr)
  const cardX = cx - cardSize / 2
  const cardY = y
  const radius = px(M.qrRadius)
  ctx.save()
  ctx.shadowColor = `rgba(0,0,0,${isLight ? 0.22 : 0.4})`
  ctx.shadowBlur = px(60)
  ctx.shadowOffsetY = px(24)
  ctx.fillStyle = '#FFFFFF'
  ctx.beginPath()
  ctx.roundRect(cardX, cardY, cardSize, cardSize, radius)
  ctx.fill()
  ctx.restore()
  ctx.save()
  ctx.shadowColor = 'transparent'
  ctx.fillStyle = '#FFFFFF'
  ctx.beginPath()
  ctx.roundRect(cardX, cardY, cardSize, cardSize, radius)
  ctx.fill()
  ctx.restore()
  if (qrImg) {
    const inner = cardSize * M.qrInner
    ctx.drawImage(qrImg, cardX + (cardSize - inner) / 2, cardY + (cardSize - inner) / 2, inner, inner)
  }
  y += cardSize + px(M.gQrCaption)

  // "Aponte a câmera do celular" (camera icon + label), centered
  ctx.font = `500 ${px(M.capFont)}px ${FONT}`
  const capText = 'Aponte a câmera do celular'
  const capTextW = ctx.measureText(capText).width
  const capTotal = px(M.capIcon) + px(M.capGap) + capTextW
  const capX = cx - capTotal / 2
  ctx.save()
  ctx.globalAlpha = isLight ? 0.7 : 0.82
  strokeIcon(ctx, capX, y + (px(M.capH) - px(M.capIcon)) / 2, px(M.capIcon), textColor, drawCameraIcon)
  ctx.fillStyle = textColor
  ctx.textAlign = 'left'
  ctx.textBaseline = 'middle'
  ctx.fillText(capText, capX + px(M.capIcon) + px(M.capGap), y + px(M.capH) / 2)
  ctx.restore()
  ctx.textBaseline = 'top'
  y += px(M.capH) + px(M.gCapFooter)

  // Footer: divider + "Reserve em segundos com" + Meiuca mark
  ctx.fillStyle = ink(0.25)
  ctx.fillRect(cx - px(M.divW) / 2, y, px(M.divW), Math.max(1, px(M.divH)))
  y += px(M.divH) + px(M.gDivCredit)

  ctx.font = `500 ${px(M.creditFont)}px ${FONT}`
  const creditTotal = px(creditModelW)
  const creditX = cx - creditTotal / 2
  ctx.save()
  ctx.globalAlpha = isLight ? 0.65 : 0.72
  ctx.fillStyle = textColor
  ctx.textAlign = 'left'
  ctx.textBaseline = 'middle'
  ctx.fillText('Reserve em segundos com', creditX, y + px(M.creditH) / 2)
  const markCx = creditX + px(creditTextW) + px(M.creditGap) + px(creditMarkW) / 2
  drawMeiucaMark(ctx, markCx, y + (px(M.creditH) - px(M.creditMarkH)) / 2, px(M.creditMarkH), textColor)
  ctx.restore()

  // State hygiene
  ctx.globalAlpha = 1
  ctx.letterSpacing = '0px'
  ctx.shadowColor = 'transparent'
  ctx.textAlign = 'left'
  ctx.textBaseline = 'top'
}

// --- Page ----------------------------------------------------------------------

/** Longest preview side in CSS pixels; the canvas keeps the chosen aspect ratio. */
const PREVIEW_MAX = 560

const SIZE_PRESETS: { label: string; w: number; h: number }[] = [
  { label: 'Quadrado · 1080×1080', w: 1080, h: 1080 },
  { label: 'Story / Reels · 1080×1920', w: 1080, h: 1920 },
  { label: 'Retrato · 1080×1350', w: 1080, h: 1350 },
  { label: 'Cartaz A4 · 2480×3508', w: 2480, h: 3508 },
]

// Square sizes (px) for the standalone-QR download.
const QR_SIZE_PRESETS = [512, 1024, 2048] as const

const clampDim = (n: number) => Math.max(320, Math.min(6000, Math.round(n) || 0))

export function QRCodeArtPage() {
  const [url, setUrl] = useState('')
  const [arenaQuery, setArenaQuery] = useState('')
  const [arenaResults, setArenaResults] = useState<VenueSearchItem[]>([])
  const [searchingArena, setSearchingArena] = useState(false)
  const [selectedArena, setSelectedArena] = useState<VenueSearchItem | null>(null)
  const [bgColor, setBgColor] = useState('#F8FAFC')
  const [bgHex, setBgHex] = useState('#F8FAFC') // text-field mirror, allows partial typing
  const [arenaLogoSrc, setArenaLogoSrc] = useState<string | null>(null)
  const [artW, setArtW] = useState(1080)
  const [artH, setArtH] = useState(1080)
  const [qrSize, setQrSize] = useState<number>(1024)

  const [arenaImg, setArenaImg] = useState<HTMLImageElement | null>(null)
  const [qrImg, setQrImg] = useState<HTMLImageElement | null>(null)

  const previewRef = useRef<HTMLCanvasElement>(null)
  const qrRef = useRef<QRCodeStyling | null>(null)

  useEffect(() => {
    document.title = 'Gerador de QR — Meiuca'
  }, [])

  // Decode the uploaded arena logo into an <img> for canvas drawing.
  useEffect(() => {
    if (!arenaLogoSrc) {
      setArenaImg(null)
      return
    }
    let cancelled = false
    loadImage(arenaLogoSrc).then((img) => {
      if (!cancelled) setArenaImg(img)
    })
    return () => {
      cancelled = true
    }
  }, [arenaLogoSrc])

  // (Re)generate the styled QR whenever the URL changes.
  useEffect(() => {
    const data = url.trim()
    if (!data) {
      setQrImg(null)
      return
    }
    let cancelled = false
    const options = buildBrandedQrOptions(data, 1000)
    if (!qrRef.current) {
      qrRef.current = new QRCodeStyling(options)
    } else {
      qrRef.current.update(options)
    }

    qrRef.current
      .getRawData('png')
      .then((raw) => {
        if (!raw || cancelled) return
        const blob = raw instanceof Blob ? raw : new Blob([raw as BlobPart], { type: 'image/png' })
        const objectUrl = URL.createObjectURL(blob)
        return loadImage(objectUrl).then((img) => {
          if (!cancelled) setQrImg(img)
          URL.revokeObjectURL(objectUrl)
        })
      })
      .catch((err) => console.error('[qr-art] falha ao gerar QR:', err))

    return () => {
      cancelled = true
    }
  }, [url])

  // Preview scaled to fit PREVIEW_MAX on its longest side, keeping the aspect.
  const previewScale = PREVIEW_MAX / Math.max(artW, artH)
  const previewW = Math.round(artW * previewScale)
  const previewH = Math.round(artH * previewScale)

  // Redraw the preview whenever any input or the dimensions change.
  useEffect(() => {
    const canvas = previewRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    drawArt(ctx, previewW, previewH, { arenaImg, qrImg, bgColor })
  }, [arenaImg, qrImg, bgColor, previewW, previewH])

  // Debounced arena search (only marketplace-visible arenas are returned).
  useEffect(() => {
    const q = arenaQuery.trim()
    if (selectedArena || q.length < 2) {
      setArenaResults([])
      return
    }
    let cancelled = false
    setSearchingArena(true)
    const timer = setTimeout(() => {
      arenaService
        .searchVenues({ query: q })
        .then((res) => {
          if (!cancelled) setArenaResults(res.venues)
        })
        .catch(() => {
          if (!cancelled) setArenaResults([])
        })
        .finally(() => {
          if (!cancelled) setSearchingArena(false)
        })
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [arenaQuery, selectedArena])

  const handleSelectArena = (arena: VenueSearchItem) => {
    setSelectedArena(arena)
    setArenaResults([])
    setArenaQuery(arena.name)
    // The booking page resolves either a slug or a UUID; prefer the slug.
    const identifier = arena.slug || arena.id
    setUrl(`${window.location.origin}/arena/${identifier}/agendamento`)
  }

  const handleClearArena = () => {
    setSelectedArena(null)
    setArenaQuery('')
    setUrl('')
  }

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => setArenaLogoSrc(reader.result as string)
    reader.readAsDataURL(file)
  }

  const handleDownload = useCallback(() => {
    const off = document.createElement('canvas')
    off.width = artW
    off.height = artH
    const ctx = off.getContext('2d')
    if (!ctx) return
    drawArt(ctx, artW, artH, { arenaImg, qrImg, bgColor })
    off.toBlob((blob) => {
      if (!blob) return
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `qr-arena-${artW}x${artH}.png`
      a.click()
      URL.revokeObjectURL(a.href)
    }, 'image/png')
  }, [arenaImg, qrImg, bgColor, artW, artH])

  // Download the styled QR on its own, re-rendered crisp at the chosen square size.
  const handleDownloadQr = useCallback(async () => {
    const data = url.trim()
    if (!data) return
    const qr = new QRCodeStyling(buildBrandedQrOptions(data, qrSize))
    const raw = await qr.getRawData('png')
    if (!raw) return
    const blob = raw instanceof Blob ? raw : new Blob([raw as BlobPart], { type: 'image/png' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    const name = selectedArena?.slug || selectedArena?.id || 'code'
    a.download = `qr-${name}-${qrSize}x${qrSize}.png`
    a.click()
    URL.revokeObjectURL(a.href)
  }, [url, qrSize, selectedArena])

  const activePreset = SIZE_PRESETS.find((p) => p.w === artW && p.h === artH)
  const canDownload = url.trim().length > 0 && qrImg !== null

  return (
    <div className="min-h-screen bg-background-primary p-6 md:p-10">
      <div className="mx-auto max-w-5xl">
        <header className="mb-8">
          <h1 className="text-2xl font-bold text-text-primary md:text-3xl">
            Gerador de QR para arenas
          </h1>
          <p className="mt-1 text-text-secondary">
            Monte a arte de divulgação com o logo da arena e o QR code da página de agendamento.
          </p>
        </header>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-[360px_1fr]">
          {/* Controls */}
          <div className="flex flex-col gap-5">
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-text-primary">Logo da arena</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleFile}
                className="block w-full text-sm text-text-secondary file:mr-3 file:cursor-pointer file:rounded-lg file:border-0 file:bg-brand-primary file:px-4 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-brand-secondary"
              />
            </label>

            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-text-primary">Arena</span>
              {selectedArena ? (
                <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-300 px-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-text-primary">{selectedArena.name}</p>
                    <p className="truncate text-xs text-text-tertiary">{url}</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleClearArena}
                    className="shrink-0 text-xs font-medium text-brand-primary hover:underline"
                  >
                    Trocar
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    type="text"
                    value={arenaQuery}
                    onChange={(e) => setArenaQuery(e.target.value)}
                    placeholder="Busque a arena pelo nome"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-text-primary outline-none focus:border-brand-primary"
                  />
                  {!selectedArena && arenaQuery.trim().length >= 2 && (
                    <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                      {searchingArena && (
                        <p className="px-3 py-2 text-sm text-text-tertiary">Buscando…</p>
                      )}
                      {!searchingArena && arenaResults.length === 0 && (
                        <p className="px-3 py-2 text-sm text-text-tertiary">Nenhuma arena encontrada.</p>
                      )}
                      {arenaResults.map((a) => (
                        <button
                          key={a.id}
                          type="button"
                          onClick={() => handleSelectArena(a)}
                          className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                        >
                          <span className="font-medium text-text-primary">{a.name}</span>
                          {(a.city || a.state) && (
                            <span className="ml-1 text-text-tertiary">
                              · {[a.city, a.state].filter(Boolean).join('/')}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <span className="text-xs text-text-tertiary">
                A arena precisa estar publicada no marketplace. O link usa o slug da arena.
              </span>
            </div>

            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-text-primary">Cor de fundo</span>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={bgColor}
                  onChange={(e) => {
                    setBgColor(e.target.value)
                    setBgHex(e.target.value.toUpperCase())
                  }}
                  aria-label="Seletor de cor"
                  className="h-10 w-12 shrink-0 cursor-pointer rounded border border-gray-300"
                />
                <input
                  type="text"
                  value={bgHex}
                  onChange={(e) => {
                    const v = e.target.value
                    setBgHex(v)
                    const norm = v.startsWith('#') ? v : `#${v}`
                    if (/^#[0-9a-fA-F]{6}$/.test(norm)) setBgColor(norm)
                  }}
                  onBlur={() => setBgHex(bgColor.toUpperCase())}
                  placeholder="#RRGGBB"
                  spellCheck={false}
                  className="w-32 rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm uppercase text-text-primary outline-none focus:border-brand-primary"
                />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-text-primary">Tamanho da arte (px)</span>
              <div className="flex flex-wrap gap-2">
                {SIZE_PRESETS.map((p) => {
                  const active = p.w === artW && p.h === artH
                  return (
                    <button
                      key={p.label}
                      type="button"
                      onClick={() => {
                        setArtW(p.w)
                        setArtH(p.h)
                      }}
                      className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                        active
                          ? 'border-brand-primary bg-brand-primary text-white'
                          : 'border-gray-300 text-text-secondary hover:border-brand-primary'
                      }`}
                    >
                      {p.label.split(' · ')[0]}
                    </button>
                  )
                })}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={320}
                  max={6000}
                  value={artW}
                  onChange={(e) => setArtW(Number(e.target.value))}
                  onBlur={() => setArtW(clampDim(artW))}
                  aria-label="Largura"
                  className="w-24 rounded-lg border border-gray-300 px-3 py-2 text-sm text-text-primary outline-none focus:border-brand-primary"
                />
                <span className="text-text-tertiary">×</span>
                <input
                  type="number"
                  min={320}
                  max={6000}
                  value={artH}
                  onChange={(e) => setArtH(Number(e.target.value))}
                  onBlur={() => setArtH(clampDim(artH))}
                  aria-label="Altura"
                  className="w-24 rounded-lg border border-gray-300 px-3 py-2 text-sm text-text-primary outline-none focus:border-brand-primary"
                />
                <button
                  type="button"
                  onClick={() => {
                    setArtW(artH)
                    setArtH(artW)
                  }}
                  title="Inverter largura e altura"
                  className="rounded-lg border border-gray-300 px-2.5 py-2 text-text-secondary hover:border-brand-primary"
                >
                  ⇄
                </button>
              </div>
              <span className="text-xs text-text-tertiary">
                {activePreset ? activePreset.label.split(' · ')[0] : 'Personalizado'} — quanto maior, mais resolução para impressão.
              </span>
            </div>

            {/* QR size — only affects the standalone-QR download */}
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-wider text-gray-400">
                Tamanho do QR (download avulso)
              </label>
              <div className="flex flex-wrap gap-2">
                {QR_SIZE_PRESETS.map((size) => (
                  <button
                    key={size}
                    type="button"
                    onClick={() => setQrSize(size)}
                    className={`rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                      qrSize === size
                        ? 'border-brand-primary text-brand-primary'
                        : 'border-gray-300 text-text-secondary hover:border-brand-primary'
                    }`}
                  >
                    {size}px
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                variant={ButtonVariant.PRIMARY}
                onClick={handleDownload}
                disabled={!canDownload}
              >
                Baixar arte
              </Button>
              <Button
                variant={ButtonVariant.SECONDARY}
                onClick={handleDownloadQr}
                disabled={!canDownload}
              >
                Baixar só o QR · {qrSize}px
              </Button>
            </div>
            {!canDownload && (
              <p className="-mt-2 text-xs text-text-tertiary">
                Busque e selecione a arena para gerar o QR code.
              </p>
            )}
          </div>

          {/* Preview */}
          <div className="flex items-start justify-center">
            <canvas
              ref={previewRef}
              width={previewW}
              height={previewH}
              style={{ width: previewW, height: previewH }}
              className="max-w-full rounded-xl border border-gray-200 shadow-sm"
            />
          </div>
        </div>
      </div>
    </div>
  )
}
